<template>
  <div ref="rootRef" class="flex editor-container relative" :class="toolbarOnTop ? 'flex-col-reverse' : 'flex-col'">
    <EditorContent
      ref="editorContentRef"
      class="editor-content cursor-text box-content"
      :class="{
        'ma-2': !isScribbleChild,
        'ma-0': isScribbleChild,
        'overflow-y-auto': isScribbleChild || editorHeight >= viewportHeight,
        'overflow-y-hidden': !isScribbleChild && editorHeight < viewportHeight,
      }"
      :editor="editor"
      :style="{
        ...style,
        maxHeight: !isScribbleChild ? `calc(100vh - ${MAX_HEIGHT_OFFSET}px)` : '200px',
      }"
    />
    <div
      v-if="isLoading"
      class="absolute inset-0 flex justify-center items-center rounded-2 pb-8 bg-base opacity-80 font-size-[18px]"
    >
      <div class="flex items-center">
        <CIcon class="mr-2" icon="cat-space" size="18px" />
        {{ loadingMessages[currentLoadingMsgIdx] }}
      </div>
    </div>
    <CEditorMenu
      v-if="editor && showMenu"
      ref="editorMenuRef"
      :allowEdit="allowEdit"
      class="w-full sticky"
      :class="{ 'pointer-events-none opacity-50': isLoading }"
      :editor="editor"
      :style="pick(style, 'borderBottomLeftRadius', 'borderBottomRightRadius')"
      @endLoading="onEndLoading"
      @openFile="(payload) => emit('openFile', payload)"
      @startLoading="onStartLoading"
    />
  </div>
</template>

<script setup lang="ts">
import { useElementSize, useWindowSize, useIntersectionObserver } from '@vueuse/core'
import { pick } from 'lodash-es'
import { ref, onBeforeUnmount, computed, onMounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useEditor, EditorContent } from '@tiptap/vue-3'
import Color from '@tiptap/extension-color'
import FontFamily from '@tiptap/extension-font-family'
import TextAlign from '@tiptap/extension-text-align'
import ListItem from '@tiptap/extension-list-item'
import Document from '@tiptap/extension-document'
import OrderedList from '@tiptap/extension-ordered-list'
import Text from '@tiptap/extension-text'
import Strike from '@tiptap/extension-strike'
import Italic from '@tiptap/extension-italic'
import Bold from '@tiptap/extension-bold'
import HardBreak from '@tiptap/extension-hard-break'
import Underline from '@tiptap/extension-underline'
import Link from '@tiptap/extension-link'
// import Mention from '@tiptap/extension-mention'
import Table from '@tiptap/extension-table'
import TableCell from '@tiptap/extension-table-cell'
import TableHeader from '@tiptap/extension-table-header'
import TableRow from '@tiptap/extension-table-row'
import History from '@tiptap/extension-history'

import BulletListExtended from '@lib/components/CRichTextEditor/extensions/bulletListExtended'
import createContentLink from '@lib/components/CRichTextEditor/extensions/contentLink'
import HeadingExtended from '@lib/components/CRichTextEditor/extensions/headingExtended'
import ParagraphExtended, { DEFAULT_FONT_SIZE } from '@lib/components/CRichTextEditor/extensions/paragraphExtended'
import TextStyleExtended from '@lib/components/CRichTextEditor/extensions/textStyleExtended'
import CEditorMenu from '@lib/components/CRichTextEditor/components/CEditorMenu.vue'
import CIcon from '@lib/components/CIcon/CIcon.vue'
// import mentionSuggestions from '@lib/components/CRichTextEditor/extensions/mentionSuggestions'
import { executeWithDoublingTime } from '@lib/util/timer'
import { TextSelection } from '@tiptap/pm/state'
import { ComponentTypes } from '@canvas-builder/types/canvas'

const ALTERNATING_LOADING_TEXTS_DURATION = 15000
const MAX_HEIGHT_OFFSET = 350
const defaultFontSizeInPx: string = DEFAULT_FONT_SIZE.toString() + 'px'

const props = withDefaults(
  defineProps<{
    id: string
    modelValue: string
    parentType?: ComponentTypes
    showMenu: boolean
    allowEdit?: boolean
    style: Record<string, any>
    flexibleToolbar?: boolean
  }>(),
  {
    allowEdit: true,
  },
)
const emit = defineEmits<{
  (e: 'openFile', payload: { fileId: string; pageIndex?: number }): void
  (e: 'update:model-value', content: string): void
  (e: 'focus'): void
  (e: 'blur'): void
}>()
const rootRef = ref<HTMLElement | null>(null)
const editorContentRef = ref<InstanceType<typeof CEditorMenu> | null>(null)
const editorMenuRef = ref<HTMLElement | null>(null)
const toolbarOnTop = ref(false)
const blur = () => {
  editor.value?.commands.blur()
}
const { t } = useI18n()
const { height: windowHeight } = useWindowSize()
const editorContentElement = computed(() => editorContentRef.value?.$el)
const { height: editorHeight } = useElementSize(editorContentElement)

const isLoading = ref(false)
const loadingMessages = [
  t('canvasUI.ai.loader.perfectingYourContent'),
  t('canvasUI.ai.loader.enhancingYourText'),
  t('canvasUI.ai.loader.gonnaLookSoGood'),
  t('canvasUI.ai.loader.longerThanExpected'),
]
const currentLoadingMsgIdx = ref(0)
let clearLoadingTimer: () => void

const isScribbleChild = computed(() => {
  return props.parentType === ComponentTypes.Scribble
})
const viewportHeight = computed(() => windowHeight.value - MAX_HEIGHT_OFFSET)

const CustomTableCell = TableCell.extend({
  addAttributes() {
    return {
      // extend the existing attributes …
      ...this.parent?.(),

      // and add a new one …
      backgroundColor: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-background-color'),
        renderHTML: (attributes) => {
          return {
            'data-background-color': attributes.backgroundColor,
            style: `background-color: ${attributes.backgroundColor}`,
          }
        },
      },
    }
  },
})

const editor = useEditor({
  editorProps: {
    handleDOMEvents: {
      keydown: (_, e) => {
        // Prevents the user from editing content
        if (!props.allowEdit) {
          e.preventDefault()
          return false
        }
      },
    },
  },
  content: props.modelValue,
  onFocus: () => emit('focus'),
  onBlur: () => emit('blur'),
  extensions: [
    // The order matters how items are reacting with each other
    HeadingExtended.configure({
      levels: [1, 2, 3],
    }),
    ParagraphExtended,
    HardBreak,
    Text,
    Color,
    FontFamily.configure({
      types: ['textStyle'],
    }),
    TextAlign.configure({
      types: ['heading', 'paragraph'],
    }),
    Document,
    TextStyleExtended,
    Underline,
    OrderedList,
    BulletListExtended,
    History.configure({
      depth: 25,
    }),
    ListItem.configure({
      HTMLAttributes: {
        style: 'margin-left: 1em',
      },
    }),
    Strike,
    Italic,
    Bold,
    createContentLink((payload) => emit('openFile', payload)),
    Link.configure({
      autolink: false,
      linkOnPaste: false,
      HTMLAttributes: {
        target: '_blank',
        rel: 'noopener noreferrer',
      },
      openOnClick: false,
    }),
    // Mention.configure({
    //   renderLabel({ options, node }) {
    //     return `${options.suggestion.char} ${node.attrs.label ?? node.attrs.id} }}`
    //   },
    //   suggestion: mentionSuggestions,
    // }),
    Table.configure({
      resizable: true,
    }),
    TableRow,
    TableHeader,
    // Default TableCell
    // TableCell,
    // Custom TableCell with backgroundColor attribute
    CustomTableCell,
  ],
  onUpdate: ({ editor }) => emit('update:model-value', editor.getHTML()),
})

const setContent = (content: string = '') => {
  if (editor.value) {
    const { from, to } = editor.value.state.selection

    editor.value.commands.setContent(content, undefined, { preserveWhitespace: true })

    if (editor.value.state.tr) {
      const tr = editor.value.state.tr
      tr.setSelection(TextSelection.create(tr.doc, from, to))
      editor.value.view.dispatch(tr)
    }
  }
}

const changeLoadingMsgIndex = () => {
  if (currentLoadingMsgIdx.value < loadingMessages.length - 1) {
    currentLoadingMsgIdx.value++
  }
}

function onStartLoading() {
  if (typeof window === 'undefined') return
  isLoading.value = true
  editor.value?.setEditable(false)
  currentLoadingMsgIdx.value = 0
  clearLoadingTimer = executeWithDoublingTime(changeLoadingMsgIndex, ALTERNATING_LOADING_TEXTS_DURATION)
}

function onEndLoading() {
  isLoading.value = false
  editor.value?.setEditable(true)
  if (clearLoadingTimer) clearLoadingTimer()
  currentLoadingMsgIdx.value = 0
}

const useFlexibleToolbar = () => {
  watch(
    () => props.showMenu,
    (newValue) => {
      if (!newValue) {
        toolbarOnTop.value = false
      }
    },
  )

  const scrollParent: HTMLElement | undefined = editorContentRef.value?.$el?.closest('.scribble-container') ?? undefined

  if (!scrollParent) {
    return
  }

  useIntersectionObserver(
    editorMenuRef,
    ([entry], observer) => {
      if (!entry || !entry.rootBounds || !rootRef.value) return

      if (entry.boundingClientRect.width === 0 && entry.boundingClientRect.height === 0) {
        requestAnimationFrame(() => {
          observer.unobserve(entry.target)
          observer.observe(entry.target)
        })

        return
      }

      toolbarOnTop.value =
        entry.rootBounds.bottom < entry.boundingClientRect.bottom ||
        entry.rootBounds.bottom < rootRef.value!.getBoundingClientRect().bottom + entry.boundingClientRect.height
    },
    {
      root: scrollParent,
      threshold: 0.0,
    },
  )
}

onMounted(() => {
  if (props.flexibleToolbar) {
    useFlexibleToolbar()
  }
})

onBeforeUnmount(() => {
  if (clearLoadingTimer) clearLoadingTimer()
})

defineExpose({ setContent, blur, toolbarOnTop })
</script>

<style lang="scss">
.editor-content * {
  box-sizing: border-box;
}

.editor-content p {
  font-size: v-bind(defaultFontSizeInPx);
}

/* Table-specific styling for tiptap */
.canvas-builder-layout {
  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    margin: 0;
  }

  .cb-text-component {
    table {
      border-collapse: collapse;
      table-layout: fixed;
      width: 100%;
      margin: 0;
      overflow: hidden;

      td,
      th {
        min-width: 1em;
        border: 2px solid #ced4da;
        padding: 3px 5px;
        vertical-align: top;
        box-sizing: border-box;
        position: relative;

        > * {
          margin-bottom: 0;
        }
      }

      th {
        font-weight: bold;
        text-align: left;
        background-color: #f1f3f5;
      }

      .selectedCell:after {
        z-index: 2;
        position: absolute;
        content: '';
        left: 0;
        right: 0;
        top: 0;
        bottom: 0;
        background: rgba(200, 200, 255, 0.4);
        pointer-events: none;
      }

      .column-resize-handle {
        position: absolute;
        right: -2px;
        top: 0;
        bottom: -2px;
        width: 4px;
        background-color: #adf;
        pointer-events: none;
      }

      p {
        margin: 0 !important;
      }
    }
  }

  .tableWrapper {
    overflow-x: auto;
  }

  .resize-cursor {
    cursor: ew-resize;
    cursor: col-resize;
  }
}
</style>
